import React, { useState, useEffect, useContext, useRef } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
import { map, isNil, includes, isEqual } from 'lodash'
import cx from 'classnames'
import { useLockBodyScroll, useLocation } from 'react-use'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'

import DigitalTransformationContext from 'contexts/DigitalTransformationContext'
import MenuButton from 'components/MenuButton'
import StickyLogo from 'components/StickyLogo'
import LinkButton from 'components/LinkButton'
import useScrollLocation from 'hooks/useScrollLocation'
import { CONTACT_ROUTE } from 'routes/constants'
import { sectionData } from './constants'

const NAV_BORDER_TRIGGER = 100

const StickyNavigation = ({ links, theme, isContactPage, isHomePage }) => {
  const isPageScrollingRef = useRef(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isButtonActive, setIsButtonActive] = useState(false)
  const { isSliderOn } = useContext(DigitalTransformationContext)
  const [activeLink, setActiveLink] = useState('')

  useLockBodyScroll(isMenuOpen)
  const { m: isMobile, sm: isTablet } = useBreakpoint()
  const { pathname } = useLocation()
  const scrollY = useScrollLocation()

  const isEarlyTrigger = isContactPage || isHomePage
  isPageScrollingRef.current =
    scrollY > (isEarlyTrigger ? 1 : NAV_BORDER_TRIGGER)
  const isProjectPage = includes(pathname, 'portfolio')
  const isDigitalSliderOn = isSliderOn && !isPageScrollingRef.current

  useEffect(() => {
    if (!isMobile && isMenuOpen) {
      setIsMenuOpen(false)
    }
  }, [isMobile])

  useEffect(() => {
    if (isProjectPage) {
      setActiveLink('/portfolio/')
    } else {
      setActiveLink(pathname)
    }
  }, [pathname, isProjectPage])

  function onMenuToggle() {
    setIsMenuOpen(!isMenuOpen)
    setIsButtonActive(!isButtonActive)
  }

  function renderLinks() {
    return map(links, (link, index) => {
      const { isExternal } = link

      if (isEqual(link.route, CONTACT_ROUTE)) {
        return (
          <li
            key={index}
            className={cx('site__nav-list-item --contact', {
              'is-digitalSlider--active': isDigitalSliderOn,
              'is-active': activeLink === link.route,
            })}
          >
            <LinkButton
              route={CONTACT_ROUTE}
              text={link.text}
              isSecondary={isTablet}
            />
          </li>
        )
      }

      return (
        <li
          key={index}
          className={cx('site__nav-list-item', {
            'is-active': activeLink === link.route,
            'is-digitalSlider--active': isDigitalSliderOn,
          })}
        >
          {isExternal ? (
            <a className="site__nav-link" href={link.route}>
              {link.text}
            </a>
          ) : (
            <Link className="site__nav-link" to={link.route}>
              {link.text}
            </Link>
          )}
        </li>
      )
    })
  }

  return (
    <nav
      className={cx('site__nav', {
        ...(!isNil(theme) && {
          [`--${theme}`]: true,
        }),
        '--is-scrolling': isPageScrollingRef.current,
        'is-active': isMenuOpen,
      })}
    >
      <div
        className={cx('site__nav-mobile-menu', {
          'is-active': isMenuOpen,
        })}
      >
        <ul className="site__nav-list">{renderLinks()}</ul>
      </div>
      <div className="site__nav-logo">
        <StickyLogo
          isMenuOpen={isMenuOpen}
          onCloseMenu={onMenuToggle}
          isSliderOn={isDigitalSliderOn}
        />
      </div>
      <div className="site__menu-button">
        <MenuButton
          onMenuToggle={onMenuToggle}
          isMenuOpen={isMenuOpen}
          isButtonActive={isButtonActive}
          setIsButtonActive={setIsButtonActive}
          isSliderOn={isDigitalSliderOn}
        />
      </div>
      <ul className="site__nav-list --desktop">{renderLinks()}</ul>
    </nav>
  )
}

StickyNavigation.defaultProps = sectionData

StickyNavigation.propTypes = {
  links: PropTypes.arrayOf(PropTypes.object),
  theme: PropTypes.string,
  isContactPage: PropTypes.bool,
  isHomePage: PropTypes.bool,
}

export default StickyNavigation
